import { Grid, Typography } from '@material-ui/core';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { IButton } from 'src/common/interfaces/IButton';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import Keypad from 'src/keypad/components/keypad';
import { priceFormat } from 'src/keypad/keypad-operation';
import { styles } from './cashback-amount-view-styles';
import FormInputView from 'src/tendering/components/process-tender/form-input-view';
import { InputField } from 'src/common/interfaces/InputField';
import { truncatePrice, getValueAsNumber } from 'src/common/helpers/number-helper';

type Props = ICashbackAmountProps;

export const showCashbackAmountPage = (props: Props): void => {
  setHomePage(<CashbackAmount {...props} />, undefined, { isReplaceablePage: true });
};

const CashbackAmount: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();
  const { onSubmit, onCancel, amountDue } = props;
  const posModeState = useSelector((state: AppState): POSModeState => state.posMode);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>('0.00');

  const handleSubmit = (): void => {
    onSubmit(value);
  };

  const handleChange = (e): void => {
    if (e.target.id === 'cashback-amount') {
      const valueWithNumber = priceFormat(e.target.value, inputRef);
      const value = truncatePrice(valueWithNumber);
      setValue(value);
    }
  };

  const getInputCollection = (): InputField[] => {
    return [
      {
        label: `${t('CashbackPaymentAmount')}`,
        autoFocus: false,
        name: 'payment-amount',
        value: truncatePrice(amountDue, false),
        dataTestIdPrefix: 'payment-amount',
      },
      {
        label: `${t('CashbackAmount')}`,
        autoFocus: true,
        name: 'cashback-amount',
        value: truncatePrice(value),
        onChange: handleChange,
        inputRef: inputRef,
        dataTestIdPrefix: 'cashback-amount',
      },
    ];
  };

  const keypadSubmitButton: IButton = {
    label: t('OK'),
    onClick: handleSubmit,
    testId: 'cashback-amount-button',
  };

  return (
    <Grid
      container
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='cashback-amount'
    >
      <Grid
        item
        className={`${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.cashbackContainer} ${classes.outerFlexDesign}`}
          data-testid={`cashback-amount-container`}
        >
          <Grid
            container
            direction='column'
            justifyContent='center'
            className={`${classes.cashbackTitleContainer} fullWidth`}
            data-testid={`cashback-amount-title`}
          >
            <Grid item className={classes.fitHeight}>
              <Grid
                container
                direction='column'
                className={`${classes.cashbackTitleContainer} fullWidth`}
              >
                <Typography
                  variant='h2'
                  className={classes.priceOverrideTitleHeader}
                  data-testid={`cashback-amount-title-text`}
                >
                  {t('EFT_Command')}
                </Typography>
                <Grid item xs={1}>
                  <Typography className={`${classes.cashbackAmountTitleBar}`} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item container className={classes.flexDesign}>
            <Grid item container xs={9}>
              <Grid container justifyContent='center'>
                <Grid item className={classes.headLine}>
                  <Typography
                    variant='h2'
                    className={classes.codeContainerTitleHeader}
                    data-testid='cashback-container-title-amount'
                  >
                    {truncatePrice(getValueAsNumber(value) + amountDue, false)}
                  </Typography>

                  <Typography className={`${classes.totalPaymentText}`}>
                    {t('TotalPaymentAmount')}
                  </Typography>

                  <Typography className={`${classes.codeTitleBar}`} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item container xs={9}>
              <Grid container justifyContent='center'>
                <Grid item md={7} lg={6}>
                  <FormInputView
                    inputCollection={getInputCollection()}
                    handleOnFocus={() => {}}
                    handleInputChange={handleChange}
                    dataTestId={''}
                    disabled={false}
                    handleInputPayment={() => {}}
                    maxLength={12}
                  />
                </Grid>
              </Grid>
              <Grid item container justifyContent='center' className={classes.selfAlign}>
                <BaseButton
                  buttonType='primary'
                  className={classes.buttonStyles}
                  onClick={onCancel}
                  data-testid='cashback-amount-cancel-button'
                >
                  {t('Exit')}
                </BaseButton>
              </Grid>
            </Grid>
            <Grid item className={classes.keypadContainer}>
              <Keypad
                visible={true}
                keyboardId={'cashback-amount'}
                noInputField={true}
                keypadTestIdPrefix={'cashback-amount-'}
                topLabelText={t('Cashback_Amount_Keyboard')}
                submitButton={keypadSubmitButton}
                disableDecimalInput={true}
                disableManualWeight={true}
                disableQuantity={true}
                replaceDecimalInput={true}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface ICashbackAmountProps {
  onSubmit: (cashbackAmount: string) => void;
  onCancel: () => void;
  amountDue: number;
}

export default React.memo(CashbackAmount);
